import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/integrations/supabase/types';
import { asJson, isPlainObject, parseChecklistItems } from '@/lib/checklistJson';
import { safeUpdateChecklist } from '@/lib/safeUpdateChecklist';
import { isNetworkFailure, offlinePhotoRef } from '@/lib/offline/offlineTypes';
import { enqueueTaskNote, enqueueTaskPhoto, enqueueToggleTask } from '@/lib/offline/offlineQueue';
import { putPhotoBlob } from '@/lib/offline/offlineDb';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  ClipboardList,
  AlertTriangle,
  Building2,
  User,
  Plus,
  Mic,
} from 'lucide-react';
import { cn, getSafeFileName } from '@/lib/utils';
import { ChecklistItem } from './ChecklistItem';
import { IssueReportSection } from './IssueReportSection';
import { PreviewBanner } from './PreviewBanner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  CLEANER_CHECKLIST_QUERY_KEY,
  CLEANER_PROPERTIES_QUERY_KEY,
  CLEANER_QUERY_DISABLED_SEGMENT,
  cleanerChecklistQueryKey,
  cleanerLocationInventoryWarmQueryKey,
  cleanerLocationIssueHintsQueryKey,
  cleanerSectionsQueryKey,
  cleanerTasksQueryKey,
} from '@/lib/cleanerQueryKeys';
import { areCleanerShellQueryParamsReady, isValidCleanerEntityId } from '@/lib/cleanerQueryGuards';
import {
  fetchCleanerSectionChecklist,
  type ChecklistTaskForSection,
  type CleanerChecklistQueryResult,
} from '@/lib/fetchCleanerSectionChecklist';
import { fetchCleanerSectionShell } from '@/lib/fetchCleanerSectionShell';
import { isCancelledCleaningTask } from '@/lib/cleanerTaskQuery';
import {
  fetchLocationInventoryWarm,
  fetchLocationIssueHints,
} from '@/lib/prefetchCleanerSectionQueries';
import {
  parseSectionDetailTabParam,
  sectionDetailTabToSearchParam,
  type SectionDetailTab,
} from '@/lib/sectionDetailTabParams';

// Stała nazwa bucketa Storage dla zdjęć zadań
const BUCKET_NAME = 'photos';

export interface ChecklistTask {
  id: string;
  name: string;
  completed: boolean;
  note?: string;
  type: 'recurring' | 'adhoc';
  task_type?: 'sop_standard' | 'coordinator_single' | 'long_term' | 'employee_extra'; // Task type from cleaning_tasks.task_type (for checklist generation logic)
  status?: 'active' | 'moved'; // Status zadania - 'moved' oznacza przeniesione na inną datę
  moved_to_task_id?: string; // ID kopii na docelową datę (dla cofnięcia przeniesienia)
  moved_to_date?: string; // ISO string daty docelowej (np. 2026-03-10T00:00:00.000Z)
  requiresPhoto?: boolean; // Legacy field - deprecated, use photo_required instead
  photoTaken?: boolean; // Legacy field - deprecated, use photo_urls.length instead
  photo_required?: boolean; // Wymagane zdjęcie (z JSON checklisty)
  min_photos?: number; // Minimalna liczba zdjęć (z JSON checklisty, domyślnie 1)
  photo_urls?: string[]; // Tablica URL-i zdjęć (z JSON checklisty)
  originTaskId?: string; // ID rekordu cleaning_tasks, z którego pochodzi zadanie (dla aktualizacji)
  is_extra_task?: boolean; // Czy zadanie jest dodatkowe (dodane przez użytkownika)
  section_id?: string; // ID sekcji (dla zadań dodatkowych)
}

interface InventoryItem {
  id: string;
  name: string;
  unit: string;
  ordered?: boolean;
}

export interface ActivityLogEntry {
  id: string;
  person: string;
  action: string;
  timestamp: string;
}

export interface SectionDetail {
  id: string;
  name: string;
  assignedTo?: string;
  isAssignedToMe: boolean;
  allowCrossAccess: boolean;
  checklistTasks: ChecklistTask[];
  activityLog: ActivityLogEntry[];
  instructionNotes?: string;
  accessCode?: string;
  taskId?: string; // ID głównego zadania dla sekcji
}

interface SectionDetailSheetProps {
  section: SectionDetail | null;
  propertyAddress: string;
  locationId: string;
  taskIds?: string[]; // Tablica ID zadań dla sekcji (cleaning_tasks.id) - wszystkie aktywne zadania
  taskId?: string; // Legacy: ID głównego zadania (dla kompatybilności wstecznej)
  orgId?: string; // ID organizacji
  isWorkStarted: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  embedded?: boolean;
  embeddedClassName?: string;
  onBack?: () => void;
  onTaskUpdated?: () => void;
}

export const SectionDetailSheet = ({ 
  section, 
  propertyAddress,
  locationId,
  taskIds,
  taskId, // Legacy - dla kompatybilności wstecznej
  orgId,
  isWorkStarted,
  open = false,
  onOpenChange,
  embedded = false,
  embeddedClassName,
  onBack,
  onTaskUpdated
}: SectionDetailSheetProps) => {
  const isVisible = embedded ? true : open;
  // Log diagnostyczny - stan overlay
  console.log('[SectionDetailSheet] render: open=', isVisible);

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionTab = useMemo(
    () => parseSectionDetailTabParam(searchParams.get('tab')),
    [searchParams],
  );

  const handleSectionTabChange = useCallback(
    (value: string) => {
      if (value !== 'tasks' && value !== 'issues') return;
      const tab = value as SectionDetailTab;
      const next = new URLSearchParams(searchParams);
      next.set('tab', sectionDetailTabToSearchParam(tab));
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const SHELL_STALE_MS = 1000 * 60 * 5;

  /** Keeps shell + issue hints + inventory warm in React Query while the user switches tabs. */
  useEffect(() => {
    if (!areCleanerShellQueryParamsReady(locationId, section?.id, orgId)) return;
    void queryClient.prefetchQuery({
      queryKey: cleanerTasksQueryKey(locationId, section!.id, orgId),
      queryFn: () => fetchCleanerSectionShell(locationId, section!.id, orgId),
      staleTime: SHELL_STALE_MS,
    });
    void queryClient.prefetchQuery({
      queryKey: cleanerLocationIssueHintsQueryKey(locationId),
      queryFn: () => fetchLocationIssueHints(locationId),
      staleTime: 60_000,
    });
    void queryClient.prefetchQuery({
      queryKey: cleanerLocationInventoryWarmQueryKey(locationId),
      queryFn: () => fetchLocationInventoryWarm(locationId),
      staleTime: 60_000,
    });
  }, [locationId, orgId, section?.id, queryClient]);

  const invalidateCleanerReactQueryCaches = useCallback(() => {
    if (!areCleanerShellQueryParamsReady(locationId, section?.id, orgId)) return;
    void queryClient.invalidateQueries({
      queryKey: cleanerTasksQueryKey(locationId, section.id, orgId),
    });
    void queryClient.invalidateQueries({
      queryKey: [CLEANER_CHECKLIST_QUERY_KEY, section.id],
    });
    void queryClient.invalidateQueries({
      queryKey: cleanerSectionsQueryKey(locationId, orgId),
    });
    void queryClient.invalidateQueries({ queryKey: [CLEANER_PROPERTIES_QUERY_KEY] });
  }, [queryClient, orgId, locationId, section?.id]);
  
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<ChecklistTask[]>([]);
  /** Avoid adding `tasks` to useLayoutEffect deps (would re-sync from stale cache over optimistic UI). */
  const tasksLengthRef = useRef(0);
  tasksLengthRef.current = tasks.length;
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Blokada odświeżania podczas zapisu
  const [taskStatus, setTaskStatus] = useState<'pending' | 'in_progress' | 'completed' | null>(null);
  const [actualTaskIds, setActualTaskIds] = useState<string[]>([]); // Tablica faktycznych task_id z bazy
  const [newTaskName, setNewTaskName] = useState<string>(''); // Stan dla nowego zadania dodatkowego
  const [isListeningExtraTask, setIsListeningExtraTask] = useState(false); // Stan nagrywania głosu dla zadania dodatkowego
  const [extraTaskToDelete, setExtraTaskToDelete] = useState<ChecklistTask | null>(null);
  const lastClickTimeRef = useRef<Record<string, number>>({});
  const tasksRef = useRef<ChecklistTask[]>([]); // Ref do aktualnego stanu tasks (dla closure w setTimeout)
  const recognitionRefExtraTask = useRef<SpeechRecognition | null>(null); // Ref dla speech recognition zadania dodatkowego
  const baseTextRefExtraTask = useRef<string>(''); // Ref dla bazowego tekstu podczas dyktowania
  const isSavingRef = useRef(false); // Ref do aktualnego stanu isSaving (dla useEffect dependencies)
  const savingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref do timeout failsafe
  const isPhotoUploadingRef = useRef(false); // Blokada czyszczenia stanu podczas uploadu zdjęć
  const refreshAbortControllerRef = useRef<AbortController | null>(null); // Ref do AbortController dla refreshDataFromDatabase
  const isRefreshingRef = useRef(false); // Ref do flagi odświeżania (zapobiega duplikacji zapytań)
  const lastHiddenTimeRef = useRef<number | null>(null); // Time-check: czas ukrycia (np. wyjście do aparatu)
  /** Tracks last `section.id` we successfully synced from React Query into local state (after setTasks). */
  const prevSyncedSectionIdRef = useRef<string | undefined>(undefined);
  /** Last section id processed by checklist sync — used to avoid wiping tasks on pending refetch when section did not change. */
  const priorSectionIdForTasksRef = useRef<string | undefined>(undefined);
  
  // Memory Shield: Stabilizacja normalizedTaskIds - zapobiega niekontrolowanemu wyzwalaniu useEffect
  // Użycie JSON.stringify zapewnia stabilność referencji nawet przy zmianie kolejności elementów
  // Zapobiega resetowaniu stanu zadań i zamykaniu arkusza przy każdym renderze
  const normalizedTaskIds = useMemo(() => {
    return taskIds || [];
  }, [JSON.stringify(taskIds)]);
  
  // Failsafe funkcja do ustawiania isSaving z automatycznym timeout
  // Skrócony timeout z 7000ms do 4000ms - UI musi "odmarznąć" szybciej
  const setSavingWithTimeout = useCallback((value: boolean, timeout = 4000) => {
    // Wyczyść poprzedni timeout jeśli istnieje
    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current);
      savingTimeoutRef.current = null;
    }
    
    setIsSaving(value);
    isSavingRef.current = value;
    
    // Jeśli ustawiamy na true, automatycznie ustaw timeout failsafe
    if (value) {
      savingTimeoutRef.current = setTimeout(() => {
        console.warn('[SectionDetailSheet] Failsafe: Automatyczny reset isSaving po timeout', timeout, 'ms');
        setIsSaving(false);
        isSavingRef.current = false;
        savingTimeoutRef.current = null;
      }, timeout);
    }
  }, []);
  
  // Synchronizacja isSavingRef ze stanem isSaving
  useEffect(() => {
    isSavingRef.current = isSaving;
  }, [isSaving]);

  const handleSheetOpenChange = useCallback((nextOpen: boolean) => {
    onOpenChange?.(nextOpen);
  }, [onOpenChange]);

  const checklistTaskIdsKey = useMemo(
    () => [...normalizedTaskIds].sort().join(","),
    [JSON.stringify(taskIds)],
  );

  const checklistQueryKey = useMemo(() => {
    if (!section?.id || !isValidCleanerEntityId(section.id) || normalizedTaskIds.length === 0) {
      return [CLEANER_CHECKLIST_QUERY_KEY, CLEANER_QUERY_DISABLED_SEGMENT, ""] as const;
    }
    return cleanerChecklistQueryKey(section.id, normalizedTaskIds);
  }, [section?.id, checklistTaskIdsKey, normalizedTaskIds.length]);

  const checklistQueryEnabled = Boolean(
    section &&
      isVisible &&
      isValidCleanerEntityId(locationId) &&
      isValidCleanerEntityId(orgId) &&
      isValidCleanerEntityId(section.id) &&
      normalizedTaskIds.length > 0,
  );

  const {
    data,
    isPending: checklistPending,
  } = useQuery<CleanerChecklistQueryResult, Error, CleanerChecklistQueryResult, QueryKey>({
    queryKey: checklistQueryKey,
    queryFn: () =>
      fetchCleanerSectionChecklist({
        sectionId: section!.id,
        normalizedTaskIds,
      }),
    enabled: checklistQueryEnabled,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
    placeholderData: (previousData) => previousData,
  });
  const checklistData: CleanerChecklistQueryResult | undefined = data;

  /** Apply server checklist from React Query; useLayoutEffect avoids one empty paint when cache hits. */
  useLayoutEffect(() => {
    if (!section?.id || !isValidCleanerEntityId(section.id)) return;

    const priorSectionId = priorSectionIdForTasksRef.current;
    const sectionIdentityChanged =
      priorSectionId !== undefined && priorSectionId !== section.id;

    const markSectionProcessed = () => {
      priorSectionIdForTasksRef.current = section.id;
    };

    if (normalizedTaskIds.length === 0) {
      setTasks([]);
      setActualTaskIds([]);
      tasksRef.current = [];
      setTaskStatus(null);
      prevSyncedSectionIdRef.current = undefined;
      markSectionProcessed();
      return;
    }

    const canApplyChecklist =
      checklistData &&
      checklistData.sectionId === section.id &&
      (checklistData.taskIdsFingerprint === checklistTaskIdsKey ||
        (tasksLengthRef.current === 0 && checklistData.tasks.length > 0));

    // Pending: nie czyść listy przy refetchu, jeśli sectionId się nie zmieniło; setTasks([]) tylko przy realnej zmianie sekcji (instancja trwa).
    if (checklistPending) {
      if (canApplyChecklist && checklistData) {
        const sectionChangedSinceLastSync = prevSyncedSectionIdRef.current !== section.id;
        if (
          sectionChangedSinceLastSync ||
          tasksLengthRef.current === 0 ||
          !(isSavingRef.current || isPhotoUploadingRef.current)
        ) {
          setTasks(checklistData.tasks as ChecklistTask[]);
          setActualTaskIds(checklistData.actualTaskIds);
          setTaskStatus(checklistData.taskStatus);
          tasksRef.current = checklistData.tasks as ChecklistTask[];
          prevSyncedSectionIdRef.current = section.id;
        }
      } else if (!canApplyChecklist && sectionIdentityChanged) {
        setTasks([]);
        setActualTaskIds([]);
        tasksRef.current = [];
        setTaskStatus(null);
        prevSyncedSectionIdRef.current = undefined;
      }
      markSectionProcessed();
      return;
    }

    if (!checklistData) {
      markSectionProcessed();
      return;
    }

    if (!canApplyChecklist) {
      markSectionProcessed();
      return;
    }

    const sectionChangedSinceLastSync = prevSyncedSectionIdRef.current !== section.id;

    if (
      !sectionChangedSinceLastSync &&
      tasksLengthRef.current > 0 &&
      (isSavingRef.current || isPhotoUploadingRef.current)
    ) {
      markSectionProcessed();
      return;
    }

    setTasks(checklistData.tasks as ChecklistTask[]);
    setActualTaskIds(checklistData.actualTaskIds);
    setTaskStatus(checklistData.taskStatus);
    tasksRef.current = checklistData.tasks as ChecklistTask[];
    prevSyncedSectionIdRef.current = section.id;
    markSectionProcessed();
  }, [
    checklistData,
    checklistPending,
    section?.id,
    checklistTaskIdsKey,
    normalizedTaskIds.length,
  ]);


  // Initialize Speech Recognition for extra task input
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition ?? window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'pl-PL';

        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          // Use base text from start of recording + new transcript to avoid duplicates
          const baseText = baseTextRefExtraTask.current.trim();
          const fullTranscript = finalTranscript + interimTranscript;
          const newText = baseText ? `${baseText} ${fullTranscript}`.trim() : fullTranscript.trim();
          
          // Ustaw nową wartość w stanie newTaskName
          setNewTaskName(newText);
        };

        recognition.onerror = (event: any) => {
          console.error('[SectionDetailSheet] Speech recognition error for extra task:', event.error);
          setIsListeningExtraTask(false);
          
          if (event.error === 'no-speech') {
            toast.info('Nie wykryto mowy. Spróbuj ponownie.');
          } else if (event.error === 'audio-capture') {
            toast.error('Nie można uzyskać dostępu do mikrofonu.');
          } else if (event.error === 'not-allowed') {
            toast.error('Brak uprawnień do mikrofonu.');
          }
        };

        recognition.onend = () => {
          setIsListeningExtraTask(false);
        };

        recognitionRefExtraTask.current = recognition;
      }
    }

    return () => {
      if (recognitionRefExtraTask.current) {
        recognitionRefExtraTask.current.stop();
      }
    };
  }, []);

  // Funkcja do obsługi głosowego wpisywania nazwy zadania dodatkowego
  const handleVoiceInputForExtraTask = () => {
    if (isSaving || !isWorkStarted) {
      toast.error('Rozpoznawanie mowy nie jest dostępne');
      return;
    }

    const SpeechRecognitionAPI =
      typeof window !== 'undefined'
        ? (window.SpeechRecognition || window.webkitSpeechRecognition)
        : undefined;

    if (!recognitionRefExtraTask.current && SpeechRecognitionAPI) {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'pl-PL';
      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        const baseText = baseTextRefExtraTask.current.trim();
        const fullTranscript = finalTranscript + interimTranscript;
        const newText = baseText ? `${baseText} ${fullTranscript}`.trim() : fullTranscript.trim();
        setNewTaskName(newText);
      };
      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('[SectionDetailSheet] Speech recognition error for extra task:', event.error);
        setIsListeningExtraTask(false);
        if (event.error === 'no-speech') {
          toast.info('Nie wykryto mowy. Spróbuj ponownie.');
        } else if (event.error === 'audio-capture') {
          toast.error('Nie można uzyskać dostępu do mikrofonu.');
        } else if (event.error === 'not-allowed') {
          toast.error('Brak uprawnień do mikrofonu.');
        }
      };
      recognition.onend = () => {
        setIsListeningExtraTask(false);
      };
      recognitionRefExtraTask.current = recognition;
    }

    const recognition = recognitionRefExtraTask.current;
    if (!recognition) {
      toast.error('Rozpoznawanie mowy nie jest dostępne w tej przeglądarce');
      return;
    }

    if (isListeningExtraTask) {
      // Stop recording
      try {
        recognition.stop();
        setIsListeningExtraTask(false);
        baseTextRefExtraTask.current = newTaskName; // Zapisz aktualną wartość jako bazę
      } catch (error) {
        console.error('[SectionDetailSheet] Błąd zatrzymania rozpoznawania:', error);
      }
    } else {
      // Start recording
      baseTextRefExtraTask.current = newTaskName; // Zapisz aktualną wartość jako bazę
      try {
        recognition.start();
        setIsListeningExtraTask(true);
        toast.info('Rozpoznawanie mowy rozpoczęte...');
      } catch (error) {
        console.error('[SectionDetailSheet] Błąd uruchomienia rozpoznawania:', error);
        toast.error('Nie udało się uruchomić rozpoznawania mowy');
        setIsListeningExtraTask(false);
      }
    }
  };

  // Naprawa "zamrażania" interfejsu po przełączeniu kart (Tab Hibernation Fix)
  // WZMOCNIENIE: Funkcja do odświeżania danych z bazy z AbortController i debounce
  const refreshDataFromDatabase = useCallback(async () => {
    // Anuluj poprzedni fetch jeśli istnieje
    if (refreshAbortControllerRef.current) {
      refreshAbortControllerRef.current.abort();
      refreshAbortControllerRef.current = null;
    }

    // Sprawdź czy już odświeżamy (zapobiega duplikacji zapytań)
    if (isRefreshingRef.current) {
      console.log('[SectionDetailSheet] Refresh już w toku, pomijam');
      return;
    }

    if (!section || !locationId || !orgId) {
      return;
    }
    
    // Stale-While-Revalidate: przy pustym normalizedTaskIds nie czyść listy podczas uploadu
    if (normalizedTaskIds.length === 0) {
      if (tasksRef.current.length > 0 || isPhotoUploadingRef.current) {
        console.log('[SectionDetailSheet] refreshDataFromDatabase: normalizedTaskIds puste, zachowuję listę');
        return;
      }
      console.log('[SectionDetailSheet] refreshDataFromDatabase: normalizedTaskIds jest puste - czyszczenie zadań');
      setTasks([]);
      tasksRef.current = [];
      setActualTaskIds([]);
      return;
    }

    isRefreshingRef.current = true;
    const abortController = new AbortController();
    refreshAbortControllerRef.current = abortController;

    try {
      setIsLoading(true);
      
      // Pobierz wszystkie zadania dla taskIds
      // Uwaga: Supabase nie wspiera bezpośrednio AbortSignal, ale możemy sprawdzić abortController.signal.aborted
      // Task type jest używany w logice generowania checklist - określa priorytet i częstotliwość zadań
      const { data: tasksData, error: tasksError } = await supabase
        .from('cleaning_tasks')
        .select('id, checklist, status, created_at, section_id, location_id, task_type, coordinator_notes')
        .in('id', normalizedTaskIds)
        .order('created_at', { ascending: false });

      // Sprawdź czy zapytanie zostało anulowane
      if (abortController.signal.aborted) {
        console.log('[SectionDetailSheet] Refresh anulowany (abort)');
        return;
      }

      if (abortController.signal.aborted) {
        console.log('[SectionDetailSheet] Refresh anulowany (po fetch)');
        return;
      }

      if (tasksError) {
        console.error('[SectionDetailSheet] Błąd pobierania checklist z bazy:', tasksError);
        return;
      }
      
      if (abortController.signal.aborted) {
        console.log('[SectionDetailSheet] Refresh anulowany (po error check)');
        return;
      }
      
      if (!tasksData || tasksData.length === 0) {
        console.log('[SectionDetailSheet] refreshDataFromDatabase: Brak zadań dla task_ids:', normalizedTaskIds);
        // Stale-While-Revalidate: nie czyść listy przy pustym wyniku lub podczas uploadu
        if (tasksRef.current.length > 0 || isPhotoUploadingRef.current) {
          console.log('[SectionDetailSheet] refreshDataFromDatabase: Zachowuję obecną listę przy pustym wyniku');
          return;
        }
        setTasks([]);
        tasksRef.current = [];
        setActualTaskIds([]);
        return;
      }

      // Zapisz faktyczne task_ids
      const currentTaskIds = tasksData.map(t => t.id);
      setActualTaskIds(currentTaskIds);

      // Zapisz status zadania
      const statuses = tasksData.map(t => t.status).filter(Boolean) as ('pending' | 'in_progress' | 'completed' | 'done')[];
      let status: 'pending' | 'in_progress' | 'completed' | null = 'pending';
      if (statuses.includes('in_progress')) status = 'in_progress';
      else if (statuses.includes('pending')) status = 'pending';
      else if (statuses.includes('completed') || statuses.includes('done')) status = 'completed';
      setTaskStatus(status);

      // Sortuj zadania
      const sortedTasks = [...tasksData].sort((a, b) => {
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (timeA !== timeB) return timeB - timeA;
        return b.id.localeCompare(a.id);
      });

      // Scal zadania
      // Przechowujemy task_type z rekordu cleaning_tasks dla logiki generowania checklist
      const uniqueChecklistMap = new Map<string, { task: any; originTaskId: string; taskTypeFromRecord?: string }>();
      const currentSectionId = section?.id;
      const normalizedSectionId = currentSectionId?.toString().toLowerCase();

      for (const taskRecord of sortedTasks) {
        if (isCancelledCleaningTask(taskRecord.status)) continue;

        const checklistData = taskRecord.checklist;
        
        // coordinator_single / employee_extra bez JSON checklisty — nazwa w coordinator_notes
        if (
          !checklistData &&
          (taskRecord.task_type === 'coordinator_single' || taskRecord.task_type === 'employee_extra')
        ) {
          const taskName = String((taskRecord as { coordinator_notes?: string | null }).coordinator_notes ?? '').trim();
          if (!taskName) continue;

          const taskSectionId = taskRecord.section_id ? String(taskRecord.section_id).toLowerCase() : null;
          const taskLocationId = taskRecord.location_id ? String(taskRecord.location_id).toLowerCase() : null;
          const normalizedLocationId = locationId ? String(locationId).toLowerCase() : null;

          const sectionMatches = Boolean(taskSectionId && normalizedSectionId && taskSectionId === normalizedSectionId);
          const locationMatches = Boolean(taskLocationId && normalizedLocationId && taskLocationId === normalizedLocationId);

          if (!sectionMatches && !locationMatches) continue;

          uniqueChecklistMap.set(taskRecord.id, {
            task: {
              id: taskRecord.id,
              name: taskName,
              task_name: taskName,
              completed: taskRecord.status === 'done',
              section_id: taskRecord.section_id,
              task_type: taskRecord.task_type,
              type: 'adhoc',
              is_extra_task: taskRecord.task_type === 'employee_extra',
            },
            originTaskId: taskRecord.id,
            taskTypeFromRecord: taskRecord.task_type,
          });
          continue;
        }
        
        if (!checklistData) continue;

        const checklistArray = parseChecklistItems(checklistData);

        // NOWA LOGIKA FILTROWANIA: Wyświetl zadania jeśli:
        // 1. section_id się zgadza (standardowy przypadek), LUB
        // 2. location_id się zgadza I zadanie jest typu coordinator_single (zadania ogólne dla budynku)
        const taskRecordLocationId = taskRecord.location_id ? String(taskRecord.location_id).toLowerCase() : null;
        const normalizedLocationId = locationId ? String(locationId).toLowerCase() : null;
        
        const sectionTasks = checklistArray.filter((item) => {
          const taskSectionId = item.section_id ? String(item.section_id).toLowerCase() : null;
          const itemLocationId = item.location_id ? String(item.location_id).toLowerCase() : taskRecordLocationId;
          
          // Sprawdź czy section_id się zgadza
          const sectionMatches = taskSectionId && normalizedSectionId && taskSectionId === normalizedSectionId;
          
          // Sprawdź czy location_id się zgadza i zadanie jest typu coordinator_single
          const locationMatches = itemLocationId && normalizedLocationId && itemLocationId === normalizedLocationId;
          const isCoordinatorTask = (item.task_type || taskRecord.task_type) === 'coordinator_single';
          
          // Wyświetl jeśli section_id się zgadza LUB (location_id się zgadza I jest coordinator_single)
          return sectionMatches || (locationMatches && isCoordinatorTask);
        });

        for (const item of sectionTasks) {
          const taskName = item.task_name || item.name || item.id || '';
          if (!taskName) continue;
          
          if (!uniqueChecklistMap.has(taskName)) {
            // Zachowaj task_type z rekordu cleaning_tasks (używany w logice generowania checklist)
            uniqueChecklistMap.set(taskName, { 
              task: item, 
              originTaskId: taskRecord.id,
              taskTypeFromRecord: taskRecord.task_type || undefined
            });
          } else {
            const existing = uniqueChecklistMap.get(taskName)!;
            const existingCompleted = existing.task.completed === true;
            const itemCompleted = item.completed === true;
            const mergedCompleted = existingCompleted || itemCompleted || false;
            
            const existingNote = existing.task.note;
            const itemNote = item.note;
            const existingNoteTrimmed = (existingNote !== undefined && existingNote !== null) ? String(existingNote).trim() : '';
            const itemNoteTrimmed = (itemNote !== undefined && itemNote !== null) ? String(itemNote).trim() : '';
            const existingNoteIsNotEmpty = existingNoteTrimmed !== '';
            const itemNoteIsNotEmpty = itemNoteTrimmed !== '';
            const mergedNote = existingNoteIsNotEmpty
              ? existingNote
              : (itemNoteIsNotEmpty ? itemNote : '');
            
            const existingPhotoUrls = existing.task.photo_urls || [];
            const itemPhotoUrls = item.photo_urls || [];
            const mergedPhotoUrls = [...new Set([...existingPhotoUrls, ...itemPhotoUrls])];
            
            // Task type: Priorytet - task_type z JSON checklisty > task_type z nowszego rekordu > task_type ze starszego rekordu
            const taskTypeFromItem = item.task_type;
            const taskTypeFromExisting = existing.taskTypeFromRecord;
            const taskTypeFromCurrentRecord = taskRecord.task_type;
            const mergedTaskType = taskTypeFromItem || taskTypeFromExisting || taskTypeFromCurrentRecord || undefined;
            
            uniqueChecklistMap.set(taskName, { 
              task: { 
                ...item,
                ...existing.task,
                completed: mergedCompleted,
                note: mergedNote,
                photo_urls: mergedPhotoUrls,
                task_type: mergedTaskType,   // Zachowaj scalony task_type (priorytet: JSON > nowszy rekord > starszy rekord)
              }, 
              originTaskId: existing.originTaskId,
              taskTypeFromRecord: mergedTaskType   // Zachowaj scalony task_type dla późniejszego użycia
            });
          }
        }
      }

      const helperMapTaskToChecklistTask = (item: any, originTaskId: string, taskTypeFromRecord?: string): ChecklistTask => {
        const completed = item.completed === true || item.completed === 'true';
        const note = (item.note !== undefined && item.note !== null) ? String(item.note) : '';
        const photo_urls = Array.isArray(item.photo_urls) ? item.photo_urls : [];
        
        // Task type: priorytet - task_type z JSON checklisty > task_type z rekordu cleaning_tasks > undefined
        const task_type = item.task_type || taskTypeFromRecord || undefined;
        
        // FIX: Jeśli task_type === 'employee_extra', automatycznie ustaw is_extra_task: true
        // Zapewnia to poprawne mapowanie zadań dodatkowych
        const is_extra_task = task_type === 'employee_extra' 
          ? true 
          : (item.is_extra_task === true || item.is_extra_task === 'true');
        
        // Status: zachowaj status z JSON checklisty (może być 'moved' dla przeniesionych zadań)
        const status = item.status === 'moved' ? 'moved' : 'active';
        
        // LOGIKA WYŚWIETLANIA NAZW ZADAŃ:
        // - Dla coordinator_single i employee_extra: użyj coordinator_notes jako głównego tytułu, fallback do checklist[0].name
        // - Dla sop_standard i long_term: użyj nazwy z item.name (item to już element z checklisty)
        let taskName = '';
        
        // KROK 1: Dla coordinator_single i employee_extra - użyj coordinator_notes
        if (task_type === 'coordinator_single' || task_type === 'employee_extra') {
          if (originTaskId) {
            const sourceTask = tasksData.find(t => t.id === originTaskId);
            if (sourceTask?.coordinator_notes) {
              taskName = sourceTask.coordinator_notes.trim();
            }
          }
          // Fallback dla coordinator_single/employee_extra jeśli brak coordinator_notes - użyj checklist[0].name lub item.name
          if (!taskName) {
            // Sprawdź czy sourceTask ma checklist z nazwą
            if (originTaskId) {
              const sourceTask = tasksData.find(t => t.id === originTaskId);
              if (sourceTask) {
                taskName = parseChecklistItems(sourceTask.checklist)[0]?.name || '';
              }
            }
            // Ostateczny fallback
            if (!taskName) {
              taskName = item.task_name || item.name || item.id || '';
            }
          }
        } else {
          // KROK 2: Dla sop_standard i long_term - użyj item.name (item to już element z checklisty)
          taskName = item.name || item.task_name || item.id || '';
        }
        
        // Dla sop_standard i long_term bez item.id: użyj originTaskId_taskName jako unikalnego ID
        const taskId = (task_type === 'coordinator_single' || task_type === 'employee_extra') && !item.id
          ? originTaskId || ''
          : (item.id || (taskName && originTaskId ? `${originTaskId}_${taskName}` : ''));
        
        return {
          id: taskId,
          name: taskName,
          completed: completed,
          note: note,
          type: (item.type || 'recurring') as 'recurring' | 'adhoc',
          task_type: task_type as 'sop_standard' | 'coordinator_single' | 'long_term' | 'employee_extra' | undefined,
          status: status as 'active' | 'moved', // FIX: Zachowaj status 'moved' z JSON checklisty
          moved_to_task_id: item.moved_to_task_id, // ID kopii na docelową datę (dla cofnięcia)
          moved_to_date: item.moved_to_date, // Data docelowa przeniesienia (ISO string)
          photo_required: item.photo_required === true || item.photo_required === 'true',
          min_photos: item.min_photos ?? (item.photo_required ? 1 : 0),
          photo_urls: photo_urls,
          originTaskId: originTaskId,
          is_extra_task: is_extra_task,
          section_id: item.section_id || section?.id,
          requiresPhoto: item.photo_required === true || item.photo_required === 'true',
          photoTaken: photo_urls.length > 0,
        };
      };

      // Konwertuj Mapę na tablicę ChecklistTask z informacją o originTaskId i task_type
      const finalTasks: ChecklistTask[] = Array.from(uniqueChecklistMap.values()).map(
        ({ task, originTaskId, taskTypeFromRecord }) => helperMapTaskToChecklistTask(task, originTaskId, taskTypeFromRecord)
      );

      if (abortController.signal.aborted) {
        console.log('[SectionDetailSheet] Refresh anulowany (przed setState)');
        return;
      }

      // PROPOZYCJA 1: Blokada uploadu zdjęć – zapobiega nadpisaniu stanu przez stare dane z bazy (Race Condition)
      if (isPhotoUploadingRef.current) {
        console.log('[SectionDetailSheet] refreshDataFromDatabase: pominięte - upload zdjęć w toku');
        return;
      }

      // KRYTYCZNE: Zawsze aktualizuj stan z finalTasks (nawet jeśli jest pusta)
      // Jeśli finalTasks jest pusta, oznacza to że nie ma zadań w bazie - wyczyść stan
      setTasks(finalTasks);
      tasksRef.current = finalTasks;
      if (section?.id && normalizedTaskIds.length > 0) {
        const fp = [...normalizedTaskIds].sort().join(",");
        queryClient.setQueryData(cleanerChecklistQueryKey(section.id, normalizedTaskIds), {
          sectionId: section.id,
          taskIdsFingerprint: fp,
          tasks: finalTasks as ChecklistTaskForSection[],
          actualTaskIds: currentTaskIds,
          taskStatus: status,
        });
      }
    } catch (error: any) {
      // Silent Failures: Wyciszenie AbortError - przeglądarka mobilna przerywa zapytania przy zmianie karty
      if (error?.name === 'AbortError' || 
          error?.name === 'DOMException' || 
          error?.message === 'signal is aborted without reason' ||
          abortController.signal.aborted) {
        console.info('[Network] Zapytanie przerwane (Tab Switch) - ignoruję błąd.');
        return;
      }
      console.error('[SectionDetailSheet] Błąd podczas odświeżania danych:', error);
    } finally {
      setIsLoading(false);
      isRefreshingRef.current = false;
      refreshAbortControllerRef.current = null;
    }
  }, [section, normalizedTaskIds, locationId, orgId, queryClient]);

  // Body Lock Cleanup i Failsafe dla visibilitychange
  // Time-check: odświeżaj dane TYLKO gdy aplikacja była w tle > 5 min (ochrona przed nadpisaniem zdjęcia z aparatu)
  useEffect(() => {
    const cleanupBodyLock = () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
      document.body.removeAttribute('data-radix-scroll-lock-scrollbar-size');
      document.body.removeAttribute('data-radix-scroll-lock-content');
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenTimeRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        console.log('[SectionDetailSheet] VisibilityState changed to visible - odblokowuję interfejs');

        setIsSaving(false);
        isSavingRef.current = false;
        if (savingTimeoutRef.current) {
          clearTimeout(savingTimeoutRef.current);
          savingTimeoutRef.current = null;
        }
        cleanupBodyLock();

        if (isPhotoUploadingRef.current) {
          console.log('[SectionDetailSheet] visibilitychange: pominięte - upload zdjęć w toku');
          return;
        }

        // Time-check: NIE odświeżaj gdy powrót z aparatu (kilka–kilkanaście sekund)
        const hiddenTime = lastHiddenTimeRef.current;
        lastHiddenTimeRef.current = null;
        if (hiddenTime) {
          const timeInBackground = Date.now() - hiddenTime;
          const FIVE_MINUTES = 5 * 60 * 1000;
          if (timeInBackground < FIVE_MINUTES) {
            console.log('[SectionDetailSheet] Zignorowano odświeżanie - krótki czas w tle:', Math.round(timeInBackground / 1000), 's');
            return;
          }
        }

        if (isVisible && section && normalizedTaskIds.length > 0 && locationId && orgId) {
          console.log('[SectionDetailSheet] Wymuszam odświeżenie danych po powrocie do karty');
          refreshDataFromDatabase();
          setTimeout(() => {
            invalidateCleanerReactQueryCaches();
            onTaskUpdated?.();
          }, 100);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanupBodyLock();
    };
  }, [isVisible, section, normalizedTaskIds.length, locationId, orgId, onTaskUpdated, refreshDataFromDatabase, invalidateCleanerReactQueryCaches]);

  // Failsafe Timeout dla isSaving - automatyczny reset po 7 sekundach
  useEffect(() => {
    if (!isSaving) return;

    // Automatyczny reset po 4 sekundach (skrócony z 7s)
    const failsafeTimeout = setTimeout(() => {
      console.warn('[SectionDetailSheet] Failsafe: Resetuję isSaving po timeout 4s');
      setIsSaving(false);
      isSavingRef.current = false;
      savingTimeoutRef.current = null;
    }, 4000);

    savingTimeoutRef.current = failsafeTimeout;

    // Reset przy visibilitychange (backup)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isSaving) {
        console.warn('[SectionDetailSheet] Failsafe: Resetuję isSaving po visibilitychange');
        setIsSaving(false);
        isSavingRef.current = false;
        clearTimeout(failsafeTimeout);
        savingTimeoutRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(failsafeTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSaving]);

  if (!section) return null;

  // Bez keepPreviousData: isPending = oczekiwanie na świeży payload dla bieżącego klucza — pokaż szkielet.
  const showChecklistSkeleton =
    checklistQueryEnabled && normalizedTaskIds.length > 0 && checklistPending;

  /** Fail-safe: lista z cache React Query, gdy lokalny stan jeszcze nie nadążył po powrocie / cache hit. */
  const displayTasks: ChecklistTask[] =
    tasks.length > 0 ? tasks : ((checklistData?.tasks as ChecklistTask[] | undefined) ?? []);

  // FIX: Filtruj zadania z statusem 'moved' z liczników - nie powinny być liczone w postępie dzisiejszych zadań
  // Zadania 'moved' są widoczne w UI, ale nie są wliczane do tasksTotal/tasksCompleted dla DNIA DZISIEJSZEGO
  const activeTasks = displayTasks.filter(t => t.status !== 'moved');
  const completedCount = activeTasks.filter(t => t.completed).length;
  // Location-based access: jedyny warunek edycji = czy pracownik ma aktywną sesję w tej lokalizacji (isWorkStarted).
  // Nie blokujemy po assigned_staff_id – grupa mobilna / zastępstwa mają pełny dostęp do zadań w lokacji.
  const isReadOnly = !isWorkStarted;
  const isInteractive = isWorkStarted;
  const canAddAdhocTask = isWorkStarted;

  // 2. POPRAWIONE ODHACZANIE (Czyści logi przy cofaniu)
  const handleToggleTask = async (stepId: string) => {
    if (!isInteractive || !orgId) return;

    const task = tasksRef.current.find(t => t.id === stepId);
    if (!task || !task.originTaskId) return;

    const targetTaskId = task.originTaskId;
    const newCompletedState = !task.completed;

    // Blokada: nie pozwalaj na ukończenie bez zdjęcia, gdy zdjęcie jest wymagane
    const photoRequired = task.photo_required === true || task.requiresPhoto === true;
    const hasPhotos = Array.isArray(task.photo_urls) && task.photo_urls.length > 0;
    if (newCompletedState && photoRequired && !hasPhotos) {
      toast.error('Zdjęcie jest wymagane!');
      return;
    }

    // Optimistic Update
    const newTasks = tasksRef.current.map(t =>
      t.id === stepId ? { ...t, completed: newCompletedState } : t
    );
    tasksRef.current = newTasks;
    setTasks(newTasks);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id ?? null;

      // Pobierz checklistę
      const { data } = await supabase.from('cleaning_tasks').select('checklist').eq('id', targetTaskId).maybeSingle();
      const currentChecklist = data?.checklist || [];

      // Aktualizuj JSON – przy ukończeniu dodaj completed_by do metadanych
      const updates: Record<string, unknown> = { completed: newCompletedState };
      if (newCompletedState && currentUserId) {
        const existingMeta = isPlainObject(currentChecklist) && isPlainObject(currentChecklist.metadata)
          ? currentChecklist.metadata
          : {};
        updates.metadata = { ...existingMeta, completed_by: currentUserId };
      }
      const updatedChecklist = safeUpdateChecklist(
        currentChecklist,
        stepId,
        updates,
        { fallbackName: task.name }
      );

      const updatePayload: Database['public']['Tables']['cleaning_tasks']['Update'] = {
        checklist: asJson(updatedChecklist),
      };
      if (newCompletedState) {
        updatePayload.status = 'done';
        updatePayload.completed_at = new Date().toISOString();
        if (currentUserId) {
          updatePayload.actual_performer_id = currentUserId;
        }
      } else {
        updatePayload.status = 'pending';
        updatePayload.completed_at = null;
        updatePayload.actual_performer_id = null;
      }

      const { error } = await supabase
        .from('cleaning_tasks')
        .update(updatePayload)
        .eq('id', targetTaskId);

      if (error) throw error;

      // Obsługa logów
      if (newCompletedState) {
        // Dodaj log
        await supabase.from('task_step_logs').insert({
          task_id: targetTaskId,
          step_name: task.name,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          is_extra_task: task.is_extra_task || false
        });
      } else {
        // USUŃ log przy odznaczeniu (Undo)
        await supabase.from('task_step_logs')
          .delete()
          .eq('task_id', targetTaskId)
          .eq('step_name', task.name);
      }

      // Odśwież liczniki na głównej liście (np. "2/5" -> "3/5")
      invalidateCleanerReactQueryCaches();
      if (onTaskUpdated) {
        await onTaskUpdated();
      }
    } catch (e) {
      console.error('Toggle error:', e);
      if (isNetworkFailure(e)) {
        try {
          await enqueueToggleTask({
            originTaskId: targetTaskId,
            stepId,
            stepName: task.name,
            completed: newCompletedState,
            orgId,
            isExtraTask: task.is_extra_task || false,
          });
          toast.info('Brak internetu. Odhaczenie zapisane i wyślemy po odzyskaniu sieci.');
          return;
        } catch (queueError) {
          console.error('Offline queue error:', queueError);
        }
      }
      toast.error('Błąd zapisu');
      // Rollback
      const rolledBackTasks = tasksRef.current.map(t =>
        t.id === stepId ? { ...t, completed: !newCompletedState } : t
      );
      tasksRef.current = rolledBackTasks;
      setTasks(rolledBackTasks);
    }
  };

  const handleNoteChange = async (taskId: string, note: string) => {
    if (!isInteractive || !orgId) return;
    const task = tasksRef.current.find(t => t.id === taskId);
    if (!task || !task.originTaskId) return;

    // Optimistic Update – od razu pokaż notatkę w UI
    const newTasks = tasksRef.current.map(t => t.id === taskId ? { ...t, note } : t);
    tasksRef.current = newTasks;
    setTasks(newTasks);

    try {
      const { data: fetchData, error: fetchError } = await supabase
        .from('cleaning_tasks')
        .select('checklist')
        .eq('id', task.originTaskId);

      const currentChecklist = (fetchData && Array.isArray(fetchData) && fetchData.length > 0 ? fetchData[0] : null)?.checklist || [];
      if (fetchError) throw fetchError;

      const updatedChecklist = safeUpdateChecklist(currentChecklist, taskId, { note }, { fallbackName: task.name });

      await supabase
        .from('cleaning_tasks')
        .update({ checklist: asJson(updatedChecklist) })
        .eq('id', task.originTaskId)
        .eq('org_id', orgId);

      toast.success('Notatka zapisana');
    } catch (e) {
      console.error('Błąd zapisu notatki', e);
      if (isNetworkFailure(e)) {
        try {
          await enqueueTaskNote({
            originTaskId: task.originTaskId,
            stepId: taskId,
            stepName: task.name,
            orgId,
            note,
          });
          toast.info('Brak internetu. Notatka zapisana offline.');
          return;
        } catch (queueError) {
          console.error('Offline note queue error:', queueError);
        }
      }
      toast.error('Nie udało się zapisać notatki');
    }
  };

  const handlePhotoTaken = (taskId: string) => {
    // ZASADA PODGLĄDU/AKTYWNEGO: Blokada interakcji gdy status !== 'in_progress'
    if (taskStatus !== 'in_progress' || !isInteractive) return;
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { ...t, photoTaken: true } : t
    ));
  };

  // 3. POPRAWIONE ZDJĘCIA (Zapobiega znikaniu listy) + zapis offline
  const handlePhotoUpload = async (taskId: string, files: File[]) => {
    if (!isInteractive || !orgId || files.length === 0) return;

    const task = tasksRef.current.find(t => t.id === taskId);
    if (!task || !task.originTaskId) return;

    const file = files[0];

    // Blokada: zapobiega odświeżaniu listy podczas uploadu (loadChecklistFromDatabase sprawdza ten ref)
    isPhotoUploadingRef.current = true;

    // Używamy lokalnego toastu zamiast blokowania całego UI
    const toastId = toast.loading('Wysyłanie zdjęcia...');
    const safeName = getSafeFileName(file, 'photo');

    try {
      // Plik już skompresowany w ChecklistItem.handleFileChange – wysyłamy bezpośrednio
      const fileName = `${task.originTaskId}/${Date.now()}-${safeName}`;

      if (!navigator.onLine) {
        const blobId = crypto.randomUUID();
        await putPhotoBlob(blobId, file);
        await enqueueTaskPhoto(
          {
            originTaskId: task.originTaskId,
            stepId: taskId,
            stepName: task.name,
            orgId,
            fileName: safeName,
          },
          blobId,
        );
        const localRef = offlinePhotoRef(blobId);
        const queuedTasks = tasksRef.current.map((item) =>
          item.id === taskId
            ? { ...item, photo_urls: [...(item.photo_urls || []), localRef], photo_required: false }
            : item,
        );
        tasksRef.current = queuedTasks;
        setTasks(queuedTasks);
        toast.info('Brak internetu. Zdjęcie zapisane i wyślemy po odzyskaniu sieci.');
        return;
      }

      const { error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(fileName);

      // Aktualizacja bazy (JSON)
      const { data } = await supabase.from('cleaning_tasks').select('checklist').eq('id', task.originTaskId).maybeSingle();
      const currentChecklist = data?.checklist || [];

      const updatedChecklist = safeUpdateChecklist(
        currentChecklist,
        taskId,
        {
          photo_urls: [...(task.photo_urls || []), publicUrl],
          photo_required: false
        },
        { fallbackName: task.name }
      );

      const { error: updateError } = await supabase
        .from('cleaning_tasks')
        .update({ checklist: asJson(updatedChecklist) })
        .eq('id', task.originTaskId);

      if (updateError) throw updateError;

      // Aktualizacja LOKALNEGO stanu (bez przeładowania strony)
      const newTasks = tasksRef.current.map(t => {
        if (t.id === taskId) {
          return {
            ...t,
            photo_urls: [...(t.photo_urls || []), publicUrl],
            photo_required: false
          };
        }
        return t;
      });
      tasksRef.current = newTasks;
      setTasks(newTasks);

      toast.success('Zdjęcie dodane');
      invalidateCleanerReactQueryCaches();

    } catch (e: unknown) {
      console.error('[handlePhotoUpload] Prawdziwy błąd:', e);
      if (e && typeof e === 'object' && ('message' in e || 'code' in e)) {
        console.error('[handlePhotoUpload] Szczegóły:', {
          message: 'message' in e ? e.message : undefined,
          code: 'code' in e ? e.code : undefined,
        });
      }
      if (isNetworkFailure(e)) {
        try {
          const blobId = crypto.randomUUID();
          await putPhotoBlob(blobId, file);
          await enqueueTaskPhoto(
            {
              originTaskId: task.originTaskId,
              stepId: taskId,
              stepName: task.name,
              orgId,
              fileName: safeName,
            },
            blobId,
          );
          const localRef = offlinePhotoRef(blobId);
          const queuedTasks = tasksRef.current.map((item) =>
            item.id === taskId
              ? { ...item, photo_urls: [...(item.photo_urls || []), localRef], photo_required: false }
              : item,
          );
          tasksRef.current = queuedTasks;
          setTasks(queuedTasks);
          toast.info('Brak internetu. Zdjęcie zapisane i wyślemy po odzyskaniu sieci.');
          return;
        } catch (queueError) {
          console.error('[handlePhotoUpload] Offline queue error:', queueError);
        }
      }
      const msg = e instanceof Error ? e.message : 'Błąd wysyłania zdjęcia';
      toast.error(msg.includes('wolne połączenie') ? msg : 'Błąd wysyłania zdjęcia');
      throw e; // Rethrow – ChecklistItem zachowa draft, użytkownik może spróbować ponownie
    } finally {
      isPhotoUploadingRef.current = false;
      toast.dismiss(toastId);
    }
    // WAŻNE: Nie wywołujemy onTaskUpdated(), żeby lista nie zniknęła!
  };

  // Usuń zdjęcie z listy – Optimistic UI (najpierw widok, potem baza)
  const handlePhotoDelete = async (stepId: string, photoUrl: string) => {
    if (taskStatus !== 'in_progress' || !isInteractive || !orgId) return;

    const currentTasks = tasksRef.current;
    const task = currentTasks.find(t => t.id === stepId);

    if (!task || !task.originTaskId) {
      console.error('[SectionDetailSheet] handlePhotoDelete: Brak task lub originTaskId dla stepId:', stepId);
      toast.error('Błąd: Nie można zidentyfikować zadania do aktualizacji');
      return;
    }

    const targetTaskId = task.originTaskId;
    const isLocalUrl = photoUrl.startsWith('data:') || photoUrl.startsWith('blob:');

    const doOptimisticUpdate = () => {
      setTasks(prev => {
        const updated = prev.map(t => {
          if (t.id === stepId) {
            const updatedUrls = (t.photo_urls ?? []).filter(url => url !== photoUrl);
            return {
              ...t,
              photo_urls: updatedUrls,
              photoTaken: updatedUrls.length > 0,
            };
          }
          return t;
        });
        tasksRef.current = updated;
        return updated;
      });
      toast.success('Zdjęcie zostało usunięte', { duration: 2000 });
    };

    // 1. Zapisz kopię zapasową stanu przed zmianą (dla rollbacku przy błędzie)
    const previousTasksState = currentTasks.map(t => ({
      ...t,
      photo_urls: t.photo_urls ? [...t.photo_urls] : [],
    }));

    doOptimisticUpdate();

    if (isLocalUrl) {
      return;
    }

    setSavingWithTimeout(true);

    try {
      const { data: fetchData, error: fetchError } = await supabase
        .from('cleaning_tasks')
        .select('checklist')
        .eq('id', targetTaskId);

      const currentTask = fetchData && Array.isArray(fetchData) && fetchData.length > 0 ? fetchData[0] : null;
      if (fetchError || !currentTask) {
        throw new Error(`Błąd pobierania danych: ${fetchError?.message || 'Brak rekordu'}`);
      }

      const currentChecklist = currentTask?.checklist || null;
      let normalizedForRead: any[] = [];
      if (Array.isArray(currentChecklist)) {
        normalizedForRead = currentChecklist;
      } else if (currentChecklist && typeof currentChecklist === 'object') {
        normalizedForRead = Object.values(currentChecklist);
      }

      let item = normalizedForRead.find((item: any) => item?.id === stepId || item?.step_id === stepId);
      if (!item && task.name) {
        const nameTrimmed = String(task.name).trim();
        if (nameTrimmed) {
          item = normalizedForRead.find((item: any) => {
            const n = item?.name ?? item?.task_name ?? '';
            return String(n).trim() === nameTrimmed;
          }) ?? undefined;
        }
      }
      const currentPhotoUrls = item?.photo_urls ?? [];
      const updatedPhotoUrls = currentPhotoUrls.filter((url: string) => url !== photoUrl);

      const updatedChecklist = safeUpdateChecklist(currentChecklist, stepId, {
        photo_urls: updatedPhotoUrls,
      }, { fallbackName: task.name });

      const { error: updateError } = await supabase
        .from('cleaning_tasks')
        .update({ checklist: asJson(updatedChecklist) })
        .eq('id', targetTaskId)
        .eq('org_id', orgId);

      if (updateError) {
        throw new Error(`Błąd zapisu do bazy danych: ${updateError.message}`);
      }

      setTimeout(() => {
        invalidateCleanerReactQueryCaches();
        onTaskUpdated?.();
      }, 100);
    } catch (error) {
      console.error('[SectionDetailSheet] Błąd usuwania zdjęcia, cofanie zmian w UI:', error);
      // ROLLBACK: Przywróć poprzedni stan – serwer odrzucił akcję (np. offline)
      setTasks(previousTasksState);
      tasksRef.current = previousTasksState;
      toast.error('Błąd połączenia. Nie udało się usunąć zdjęcia z serwera.');
    } finally {
      setTimeout(() => setSavingWithTimeout(false), 400);
    }
  };

  // Dodawanie zadania dodatkowego
  const handleAddTask = async () => {
    // ZASADA PODGLĄDU/AKTYWNEGO: Blokada interakcji gdy sekcja jest tylko do odczytu
    // Pozwalamy na dodawanie zadań nawet gdy status === 'pending' (pracownik może dodać zadanie przed rozpoczęciem pracy)
    if (isReadOnly || !orgId || !section) return;
    
    const trimmedTaskName = newTaskName.trim();
    if (!trimmedTaskName) {
      toast.error('Wpisz nazwę zadania');
      return;
    }

    // Self-Healing State: Ustaw blokadę zapisu z automatycznym failsafe timeout (4s)
    setSavingWithTimeout(true);

    try {
      // Pobierz aktualnego użytkownika
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Błąd pobierania użytkownika');
      }

      // Utwórz nowy rekord w cleaning_tasks z task_type = 'employee_extra'
      const now = new Date().toISOString();
      const { data: newTaskDataArr, error: insertError } = await supabase
        .from('cleaning_tasks')
        .insert({
          org_id: orgId,
          location_id: locationId,
          section_id: section.id,
          assigned_staff_id: user.id,
          task_type: 'employee_extra',
          status: 'pending',
          coordinator_notes: trimmedTaskName, // Nazwa zadania w coordinator_notes
          scheduled_at: now,
        })
        .select('id');

      if (insertError) {
        throw new Error(`Błąd zapisu: ${insertError.message}`);
      }

      const newTaskData = newTaskDataArr && Array.isArray(newTaskDataArr) && newTaskDataArr.length > 0 ? newTaskDataArr[0] : null;
      if (!newTaskData || !newTaskData.id) {
        throw new Error('Brak potwierdzenia zapisu z bazy danych');
      }

      // Zapisz do task_step_logs
      const { error: logError } = await supabase
        .from('task_step_logs')
        .insert({
          task_id: newTaskData.id,
          user_id: user.id,
          step_name: trimmedTaskName,
          photo_url: null,
          is_extra_task: true,
        });

      if (logError) {
        console.error('[SectionDetailSheet] Błąd zapisu do logów:', logError);
        // Nie przerywamy - zadanie zostało utworzone
      }

      // Wyczyść pole input
      setNewTaskName('');

      // Optimistic UI: natychmiast dodaj zadanie do listy
      const newTask: ChecklistTask = {
        id: newTaskData.id,
        name: trimmedTaskName,
        completed: false,
        section_id: section.id,
        task_type: 'employee_extra',
        type: 'adhoc',
        note: '',
        photo_required: false,
        photo_urls: [],
        originTaskId: newTaskData.id,
        is_extra_task: true,
      };
      tasksRef.current = [newTask, ...tasksRef.current];
      setTasks(prev => [newTask, ...prev]);

      toast.success('Zadanie dodatkowe dodane', { duration: 2000 });

      // Odśwież liczniki i dane z bazy
      invalidateCleanerReactQueryCaches();
      onTaskUpdated?.();
    } catch (error) {
      console.error('[SectionDetailSheet] Błąd podczas dodawania zadania:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      toast.error(`Błąd podczas dodawania zadania: ${errorMessage}`);
    } finally {
      // Self-Healing State: ZAWSZE zwolnij blokadę zapisu - gwarantuje że interfejs będzie responsywny
      setTimeout(() => {
        setSavingWithTimeout(false);
      }, 400);
    }
  };

  // Przełożenie zadania na wybraną datę (Clone & Mark Moved)
  // Tworzy kopię na targetDate, oryginał pozostaje z statusem 'moved'
  const handleRescheduleTask = async (taskId: string, targetDate: Date) => {
    if (!isInteractive || !orgId || !locationId) return;

    const task = tasksRef.current.find(t => t.id === taskId);
    if (!task || !task.originTaskId) {
      toast.error('Nie można przenieść tego zadania');
      return;
    }

    if (task.task_type !== 'sop_standard') {
      toast.warning('Tylko standardowe zadania można przełożyć');
      return;
    }

    if (task.status === 'moved') {
      toast.info('To zadanie jest już przeniesione');
      return;
    }

    const targetTaskId = task.originTaskId;
    const taskName = task.name;
    if (!taskName) {
      toast.error('Nie można zidentyfikować zadania - brak nazwy');
      return;
    }

    const targetDateNorm = new Date(targetDate);
    targetDateNorm.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (targetDateNorm.getTime() <= today.getTime()) {
      toast.error('Wybierz datę w przyszłości');
      return;
    }
    const targetDateISO = targetDateNorm.toISOString();
    const targetDateEnd = new Date(targetDateNorm);
    targetDateEnd.setHours(23, 59, 59, 999);
    const targetDateEndISO = targetDateEnd.toISOString();

    setSavingWithTimeout(true);

    try {
      // 1. Pobierz oryginalny rekord i znajdź element checklisty
      const { data: originalRecord, error: fetchError } = await supabase
        .from('cleaning_tasks')
        .select('id, checklist, location_id, section_id, task_type')
        .eq('id', targetTaskId)
        .eq('org_id', orgId)
        .single();

      if (fetchError || !originalRecord) {
        throw new Error(`Błąd pobierania zadania: ${fetchError?.message || 'Brak rekordu'}`);
      }

      let normalizedChecklist: any[] = [];
      if (Array.isArray(originalRecord.checklist)) {
        normalizedChecklist = [...originalRecord.checklist];
      } else if (originalRecord.checklist && typeof originalRecord.checklist === 'object') {
        normalizedChecklist = Object.values(originalRecord.checklist);
      }

      const itemIndex = normalizedChecklist.findIndex((item: any) =>
        item?.id === taskId || item?.name === taskName || item?.task_name === taskName
      );

      if (itemIndex === -1) {
        throw new Error('Nie znaleziono elementu w checkliście');
      }

      const sourceItem = normalizedChecklist[itemIndex];
      const sourceTemplateId = sourceItem?.metadata?.source_template_id;

      // 2. WERYFIKACJA DUPLIKATÓW: Czy zadanie już istnieje na wybraną datę?
      const { data: existingTargetTasks, error: existingTasksError } = await supabase
        .from('cleaning_tasks')
        .select('id, checklist, task_type, section_id')
        .eq('location_id', locationId)
        .eq('org_id', orgId)
        .eq('task_type', 'sop_standard')
        .eq('section_id', section?.id || null)
        .gte('scheduled_at', targetDateISO)
        .lte('scheduled_at', targetDateEndISO);

      if (existingTasksError) {
        throw new Error(`Błąd sprawdzania istniejących zadań: ${existingTasksError.message}`);
      }

      let taskAlreadyExists = false;
      if (existingTargetTasks && existingTargetTasks.length > 0) {
        for (const existingTask of existingTargetTasks) {
          const existingChecklist = existingTask.checklist;
          if (!existingChecklist) continue;

          let normList: any[] = [];
          if (Array.isArray(existingChecklist)) normList = existingChecklist;
          else if (typeof existingChecklist === 'object' && existingChecklist !== null) normList = Object.values(existingChecklist);

          const found = normList.some((item: any) => {
            const itemName = item.name || item.task_name || '';
            const itemSourceId = item.metadata?.source_template_id;
            if (sourceTemplateId && itemSourceId && itemSourceId === sourceTemplateId) return true;
            return itemName === taskName;
          });

          if (found) {
            taskAlreadyExists = true;
            break;
          }
        }
      }

      if (taskAlreadyExists) {
        toast.error('To zadanie jest już w harmonogramie na wybraną datę');
        return;
      }

      // 3. Przygotuj kopię elementu (bez status moved)
      const itemCopy = { ...sourceItem };
      delete itemCopy.status;
      delete itemCopy.moved_to_task_id;

      // 4. INSERT: Utwórz kopię na wybraną datę
      const { data: newRecord, error: insertError } = await supabase
        .from('cleaning_tasks')
        .insert({
          location_id: originalRecord.location_id,
          section_id: originalRecord.section_id || null,
          org_id: orgId,
          task_type: 'sop_standard',
          scheduled_at: targetDateISO,
          checklist: [itemCopy],
          status: 'pending',
          priority: 'medium',
        })
        .select('id')
        .single();

      if (insertError || !newRecord) {
        throw new Error(`Błąd tworzenia kopii: ${insertError?.message || 'Brak ID'}`);
      }

      const newRecordId = newRecord.id;

      // 5. UPDATE: Oznacz oryginał jako przeniesiony (status w checkliście + data docelowa)
      const updatedChecklist = safeUpdateChecklist(
        originalRecord.checklist,
        taskId,
        { status: 'moved', moved_to_task_id: newRecordId, moved_to_date: targetDateISO },
        { fallbackName: taskName }
      );

      const { error: updateError } = await supabase
        .from('cleaning_tasks')
        .update({ checklist: asJson(updatedChecklist) })
        .eq('id', targetTaskId)
        .eq('org_id', orgId);

      if (updateError) {
        await supabase.from('cleaning_tasks').delete().eq('id', newRecordId).eq('org_id', orgId);
        throw new Error(`Błąd aktualizacji oryginału: ${updateError.message}`);
      }

      // 6. OPTIMISTIC UPDATE: Zadanie pozostaje na liście jako "Przeniesione"
      const optimisticTasks = tasksRef.current.map(t =>
        t.id === taskId ? { ...t, status: 'moved' as const, moved_to_task_id: newRecordId, moved_to_date: targetDateISO } : t
      );
      tasksRef.current = optimisticTasks;
      setTasks(optimisticTasks);

      const dateStr = targetDateNorm.toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' });
      toast.success(`Zadanie przełożone na ${dateStr}`, { duration: 2000 });

      setTimeout(() => {
        invalidateCleanerReactQueryCaches();
        onTaskUpdated?.();
      }, 500);
    } catch (error) {
      console.error('[SectionDetailSheet] Błąd podczas przenoszenia zadania:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      toast.error(`Błąd podczas przenoszenia zadania: ${errorMessage}`);
    } finally {
      setTimeout(() => setSavingWithTimeout(false), 400);
    }
  };

  // Cofanie przeniesienia zadania (Undo Move)
  // Cofanie przeniesienia zadania (Undo Move) - usuwa kopię, przywraca oryginał
  const handleUndoMove = async (taskId: string) => {
    if (!isInteractive || !orgId || !locationId) return;

    const task = tasksRef.current.find(t => t.id === taskId);
    if (!task || !task.originTaskId) {
      toast.error('Nie można cofnąć przeniesienia tego zadania');
      return;
    }

    if (task.status !== 'moved') {
      toast.warning('To zadanie nie jest przeniesione');
      return;
    }

    const movedToTaskId = task.moved_to_task_id;
    if (!movedToTaskId) {
      toast.error('Nie można cofnąć – brak informacji o kopii (zadanie przeniesione starszą wersją)');
      return;
    }

    const targetTaskId = task.originTaskId;
    const taskName = task.name;
    if (!taskName) {
      toast.error('Nie można zidentyfikować zadania - brak nazwy');
      return;
    }

    // OPTIMISTIC UPDATE
    const optimisticTasks = tasksRef.current.map(t =>
      t.id === taskId ? { ...t, status: undefined, moved_to_task_id: undefined, moved_to_date: undefined } : t
    );
    tasksRef.current = optimisticTasks;
    setTasks(optimisticTasks);

    setSavingWithTimeout(true);

    try {
      // 1. DELETE: Usuń kopię na jutro
      const { error: deleteError } = await supabase
        .from('cleaning_tasks')
        .delete()
        .eq('id', movedToTaskId)
        .eq('org_id', orgId);

      if (deleteError) {
        throw new Error(`Błąd usuwania kopii: ${deleteError.message}`);
      }

      // 2. UPDATE: Przywróć oryginał – usuń status 'moved' i moved_to_task_id z checklisty
      const { data: originalRecord, error: fetchError } = await supabase
        .from('cleaning_tasks')
        .select('checklist')
        .eq('id', targetTaskId)
        .eq('org_id', orgId)
        .single();

      if (fetchError || !originalRecord) {
        throw new Error(`Błąd pobierania oryginału: ${fetchError?.message || 'Brak rekordu'}`);
      }

      let normalizedArray: any[] = [];
      if (Array.isArray(originalRecord.checklist)) {
        normalizedArray = [...originalRecord.checklist];
      } else if (originalRecord.checklist && typeof originalRecord.checklist === 'object') {
        normalizedArray = Object.values(originalRecord.checklist);
      }

      const idx = normalizedArray.findIndex((item: any) =>
        item?.id === taskId || item?.name === taskName || item?.task_name === taskName
      );
      if (idx !== -1) {
        const item = { ...normalizedArray[idx] };
        delete item.status;
        delete item.moved_to_task_id;
        delete item.moved_to_date;
        normalizedArray[idx] = item;
      }

      const { error: updateError } = await supabase
        .from('cleaning_tasks')
        .update({ checklist: normalizedArray })
        .eq('id', targetTaskId)
        .eq('org_id', orgId);

      if (updateError) {
        throw new Error(`Błąd przywracania oryginału: ${updateError.message}`);
      }

      toast.success('Przeniesienie zadania cofnięte', { duration: 2000 });

      setTimeout(() => {
        invalidateCleanerReactQueryCaches();
        onTaskUpdated?.();
      }, 500);
    } catch (error) {
      console.error('[SectionDetailSheet] Błąd podczas cofania przeniesienia zadania:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      const rolledBackTasks = tasksRef.current.map(t =>
        t.id === taskId ? { ...t, status: 'moved' as const, moved_to_task_id: movedToTaskId } : t
      );
      tasksRef.current = rolledBackTasks;
      setTasks(rolledBackTasks);
      toast.error(`Błąd podczas cofania przeniesienia: ${errorMessage}`);
    } finally {
      setTimeout(() => setSavingWithTimeout(false), 400);
    }
  };

  // Cofanie ukończenia zadania (zmiana statusu z 'done' na 'pending')
  const handleUndoComplete = async (taskId: string) => {
    if (!isInteractive || !orgId) return;
    
    const currentTasks = tasksRef.current;
    const task = currentTasks.find(t => t.id === taskId);
    
    if (!task || !task.originTaskId) {
      console.error('[SectionDetailSheet] handleUndoComplete: Brak task lub originTaskId dla stepId:', taskId);
      toast.error('Błąd: Nie można zidentyfikować zadania do aktualizacji');
      return;
    }
    
    const targetTaskId = task.originTaskId;
    console.log('[SectionDetailSheet] handleUndoComplete: Cofanie ukończenia dla stepId:', taskId, 'originTaskId:', targetTaskId);

    setSavingWithTimeout(true);

    try {
      // Pobierz aktualny stan checklist z bazy – bez .single()/.maybeSingle() (PGRST116)
      const { data: fetchData, error: fetchError } = await supabase
        .from('cleaning_tasks')
        .select('checklist, status')
        .eq('id', targetTaskId);

      const currentTask = fetchData && Array.isArray(fetchData) && fetchData.length > 0 ? fetchData[0] : null;
      if (fetchError || !currentTask) {
        throw new Error(`Błąd pobierania danych: ${fetchError?.message || 'Brak rekordu'}`);
      }

      // Zaktualizuj stan checklist w JSONB - zmień completed na false
      const currentChecklist = currentTask?.checklist || null;
      const updatedChecklist = safeUpdateChecklist(currentChecklist, taskId, {
        completed: false,
      }, { fallbackName: task.name });

      // Zapisz zaktualizowany checklist do bazy danych
      const { data: updateData, error: updateError } = await supabase
        .from('cleaning_tasks')
        .update({ checklist: asJson(updatedChecklist) })
        .eq('id', targetTaskId)
        .eq('org_id', orgId)
        .select();

      if (updateError) {
        throw new Error(`Błąd zapisu: ${updateError.message}`);
      }

      if (!updateData || updateData.length === 0) {
        throw new Error('Brak potwierdzenia zapisu z bazy danych');
      }

      // Jeśli status rekordu cleaning_tasks to 'done', zmień go na 'pending'
      if (currentTask.status === 'done') {
        const { error: statusError } = await supabase
          .from('cleaning_tasks')
          .update({ status: 'pending' })
          .eq('id', targetTaskId)
          .eq('org_id', orgId);

        if (statusError) {
          console.warn('[SectionDetailSheet] Nie udało się zmienić statusu rekordu:', statusError);
          // Nie przerywamy - checklist został zaktualizowany
        }
      }

      // OPTIMISTIC UPDATE: Zaktualizuj lokalny stan
      const optimisticTasks = tasksRef.current.map(t => 
        t.id === taskId ? { ...t, completed: false } : t
      );
      tasksRef.current = optimisticTasks;
      setTasks(optimisticTasks);

      toast.success('Ukończenie zadania cofnięte', { duration: 2000 });

      setTimeout(() => {
        invalidateCleanerReactQueryCaches();
        onTaskUpdated?.();
      }, 500);
    } catch (error) {
      console.error('[SectionDetailSheet] Błąd podczas cofania ukończenia zadania:', error);
      const errorMessage = error instanceof Error ? error.message : 'Nieznany błąd';
      
      // OPTIMISTIC UPDATE ROLLBACK: Cofnij zmianę lokalnego stanu
      const rolledBackTasks = tasksRef.current.map(t => 
        t.id === taskId ? { ...t, completed: true } : t
      );
      tasksRef.current = rolledBackTasks;
      setTasks(rolledBackTasks);
      
      toast.error(`Błąd podczas cofania ukończenia: ${errorMessage}`);
    } finally {
      setTimeout(() => {
        setSavingWithTimeout(false);
      }, 400);
    }
  };

  // 1. POPRAWIONE USUWANIE (Obsługuje błąd 23503 przez usunięcie wszystkich powiązań)
  const handleDeleteExtraTask = (taskId: string) => {
    const task = tasksRef.current.find((item) => item.id === taskId);
    if (!task) {
      toast.error('Nie znaleziono zadania do usunięcia.');
      return;
    }
    const canDelete =
      task.task_type === 'employee_extra' ||
      task.task_type === 'coordinator_single' ||
      task.is_extra_task === true;
    if (!canDelete) {
      toast.info('Usuwanie punktów SOP nie jest obsługiwane w tym trybie.');
      return;
    }
    setExtraTaskToDelete(task);
  };

  const confirmDeleteExtraTask = async () => {
    const task = extraTaskToDelete;
    if (!task || !orgId) return;

    const targetId = task.originTaskId || task.id;
    setSavingWithTimeout(true);
    try {
      const { error: stepLogsError } = await supabase.from('task_step_logs').delete().eq('task_id', targetId);
      if (stepLogsError) {
        console.error('[SectionDetailSheet] Błąd usuwania task_step_logs', stepLogsError);
      }
      const { error: execLogsError } = await supabase.from('task_execution_logs').delete().eq('task_id', targetId);
      if (execLogsError) {
        console.error('[SectionDetailSheet] Błąd usuwania task_execution_logs', execLogsError);
      }

      const { data: deletedRows, error } = await supabase
        .from('cleaning_tasks')
        .delete()
        .eq('id', targetId)
        .eq('org_id', orgId)
        .select('id');
      if (error) {
        const isFkError =
          error.code === '23503' ||
          error.message?.includes('foreign key') ||
          error.message?.includes('violates foreign key');
        if (isFkError) {
          const { data: cancelledRows, error: cancelError } = await supabase
            .from('cleaning_tasks')
            .update({ status: 'cancelled' })
            .eq('id', targetId)
            .eq('org_id', orgId)
            .select('id');
          if (cancelError) throw cancelError;
          if (!cancelledRows?.length) {
            throw new Error('Usunięcie nie zostało potwierdzone przez serwer');
          }
        } else {
          throw error;
        }
      } else if (!deletedRows?.length) {
        throw new Error('Usunięcie nie zostało potwierdzone przez serwer');
      }

      const newTasks = tasksRef.current.filter(
        (item) => item.id !== task.id && item.originTaskId !== targetId,
      );
      tasksRef.current = newTasks;
      setTasks(newTasks);
      setActualTaskIds((prev) => prev.filter((id) => id !== targetId));
      setExtraTaskToDelete(null);

      queryClient.setQueriesData(
        { queryKey: [CLEANER_CHECKLIST_QUERY_KEY, section?.id] },
        (old: { tasks?: ChecklistTask[]; actualTaskIds?: string[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            tasks: (old.tasks ?? []).filter(
              (item) => item.id !== task.id && item.originTaskId !== targetId,
            ),
            actualTaskIds: (old.actualTaskIds ?? []).filter((id) => id !== targetId),
          };
        },
      );

      toast.success('Zadanie zostało usunięte.');
      invalidateCleanerReactQueryCaches();
      onTaskUpdated?.();
    } catch (error) {
      console.error('[Delete Task Error]:', error);
      toast.error('Nie udało się usunąć zadania. Spróbuj ponownie.');
    } finally {
      setSavingWithTimeout(false);
    }
  };

  // FIX: Zadania z statusem 'moved' muszą pozostać widoczne (ale wyszarzone)
  // Pracownik powinien móc cofnąć przeniesienie zadania
  const visibleTasks = displayTasks; // Wszystkie zadania są widoczne, w tym 'moved'

  // Sortuj zadania według priorytetu task_type:
  // 1. PILNE: coordinator_single (na górze, wyróżnione)
  // 2. STANDARD: sop_standard (środek)
  // 3. DODATKOWE: is_extra_task === true (pod standardowymi)
  // 4. OKRESOWE: long_term (na dole, wyszarzone)
  const sortedTasks = [...visibleTasks].sort((a, b) => {
    // Funkcja pomocnicza do określenia priorytetu zadania
    const getPriority = (task: ChecklistTask): number => {
      // 1. coordinator_single - najwyższy priorytet
      if (task.task_type === 'coordinator_single') return 1;
      // 2. sop_standard - standardowy priorytet (ale nie is_extra_task)
      if (task.task_type === 'sop_standard' && !task.is_extra_task) return 2;
      // 3. employee_extra lub is_extra_task - zadania dodatkowe
      if (task.task_type === 'employee_extra' || task.is_extra_task === true) return 3;
      // 4. long_term - najniższy priorytet
      if (task.task_type === 'long_term') return 4;
      // 5. Inne - domyślnie standardowy priorytet
      return 2;
    };

    const priorityA = getPriority(a);
    const priorityB = getPriority(b);
    
    // Sortuj według priorytetu
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Zachowaj oryginalną kolejność dla zadań tego samego priorytetu
    return 0;
  });

  // Podziel zadania na grupy według task_type
  // FIX: Filtry są rozłączne - coordinatorTasks wykluczają zadania dodatkowe, extraTasks wykluczają zadania koordynatora
  // WAŻNE: Zadania coordinator_single i employee_extra muszą być widoczne ZAWSZE, niezależnie od innych warunków
  const coordinatorTasks = sortedTasks.filter(t => 
    t.task_type === 'coordinator_single'
  );
  const standardTasks = sortedTasks.filter(t => 
    t.task_type === 'sop_standard' && 
    !t.is_extra_task
  );
  const extraTasks = sortedTasks.filter(t => 
    t.task_type === 'employee_extra' || 
    (t.is_extra_task === true && t.task_type !== 'coordinator_single' && t.task_type !== 'sop_standard' && t.task_type !== 'long_term')
  );
  const longTermTasks = sortedTasks.filter(t => 
    t.task_type === 'long_term' && 
    !t.is_extra_task
  );

  // DEBUG: Sprawdź, czy wszystkie zadania trafiają do którejś grupy
  const allGroupedTasks = [...coordinatorTasks, ...standardTasks, ...extraTasks, ...longTermTasks];
  const ungroupedTasks = sortedTasks.filter(t => !allGroupedTasks.some(gt => gt.id === t.id));
  
  // DEBUG: Loguj wszystkie zadania i ich przypisanie do grup
  console.log('[SectionDetailSheet] DEBUG - Zadania i grupy:', {
    totalTasks: sortedTasks.length,
    coordinatorTasks: coordinatorTasks.length,
    standardTasks: standardTasks.length,
    extraTasks: extraTasks.length,
    longTermTasks: longTermTasks.length,
    ungroupedTasks: ungroupedTasks.length,
    allTasks: sortedTasks.map(t => ({ id: t.id, name: t.name, task_type: t.task_type, is_extra_task: t.is_extra_task, status: t.status }))
  });
  
  if (ungroupedTasks.length > 0) {
    console.warn('[SectionDetailSheet] Nieprzypisane zadania:', ungroupedTasks.map(t => ({ id: t.id, name: t.name, task_type: t.task_type, is_extra_task: t.is_extra_task })));
    // Przypisz nieprzypisane zadania do odpowiedniej grupy na podstawie task_type
    ungroupedTasks.forEach(task => {
      if (task.task_type === 'coordinator_single') {
        coordinatorTasks.push(task);
      } else if (task.task_type === 'employee_extra' || task.is_extra_task === true) {
        extraTasks.push(task);
      } else if (task.task_type === 'sop_standard') {
        standardTasks.push(task);
      } else if (task.task_type === 'long_term') {
        longTermTasks.push(task);
      } else {
        // Domyślnie przypisz do standardTasks, jeśli nie ma określonego typu
        console.warn('[SectionDetailSheet] Zadanie bez określonego typu przypisane do standardTasks:', task);
        standardTasks.push(task);
      }
    });
  }

  return (
    <div className={embedded ? (embeddedClassName || "min-h-screen bg-canvas flex flex-col") : ""}>
      <div className={embedded ? "w-full max-w-3xl mx-auto p-4 sm:p-6" : "w-full sm:max-w-lg overflow-y-auto border-none shadow-xl"}>
        <header className="pb-4">
          <div className="work-page-header">
            <Button 
              variant="outline" 
              size="sm" 
              className="mb-3 bg-white dark:bg-slate-800 hover:bg-slate-100"
              onClick={() => {
                if (embedded) {
                  onBack?.();
                  return;
                }
                handleSheetOpenChange(false);
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {embedded ? 'Wróć' : t('property.returnToSection')}
            </Button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {section.name}
            </h1>
            <p className="mt-1 text-sm font-medium text-slate-600 dark:text-slate-300">
              {propertyAddress}
            </p>
            
            {/* Assigned to badge */}
            {section.assignedTo && !section.isAssignedToMe && (
              <Badge variant="outline" className="mt-2 text-xs font-normal gap-1">
                <User className="h-3 w-3" />
                {t('property.assignedTo')}: {section.assignedTo}
              </Badge>
            )}

            {/* Read-only badge: tylko gdy praca nie została jeszcze rozpoczęta (tryb podglądu) */}
            {isReadOnly && (
              <Badge variant="secondary" className="mt-2 ml-2 text-xs">
                {t('property.readOnly')}
              </Badge>
            )}
          </div>
        </header>

        <div className="space-y-5 pb-6">
          {/* Preview Banner - only when not yet started (tryb podglądu); after "Rozpocznij pracę" never show */}
          {!isWorkStarted && taskStatus === 'pending' && <PreviewBanner />}

          {/* Tabs: checklist + issues — forceMount + CSS hide (jak Admin) zapobiega odmontowaniu i utracie stanu */}
          <Tabs value={sectionTab} onValueChange={handleSectionTabChange} className="w-full">
            <TabsList className="work-tabs grid w-full grid-cols-2 h-auto gap-1">
              <TabsTrigger
                value="tasks"
                className="work-tab group flex flex-col items-stretch gap-0.5 py-3 px-3 h-auto whitespace-normal text-left"
              >
                <span className="flex items-center gap-2 font-semibold">
                  <ClipboardList className="h-4 w-4 shrink-0" />
                  {t('sections.checklist')}
                </span>
                <span className="text-xs font-medium text-slate-500 group-data-[state=active]:text-primary-foreground/80 dark:text-slate-400">
                  {completedCount}/{activeTasks.length} {t('section.tasksCompleted')}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="issues"
                className="work-tab group flex flex-col items-stretch gap-0.5 py-3 px-3 h-auto whitespace-normal text-left"
              >
                <span className="flex items-center gap-2 font-semibold">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {t('sections.issues')}
                </span>
                <span className="text-xs font-medium text-slate-500 group-data-[state=active]:text-primary-foreground/80 dark:text-slate-400">
                  {t('section.reportIssueInSection')}
                </span>
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="tasks"
              forceMount
              className="mt-4 space-y-4 outline-none data-[state=inactive]:hidden"
            >
              <div className={cn(
                "relative rounded-xl overflow-hidden transition-all border-2",
                "bg-canvas-well dark:bg-zinc-800/80 border-zinc-300 dark:border-zinc-700/60"
              )}>
                <div className="px-4 pb-4 pt-4">
                  <div className="space-y-4">
                    {showChecklistSkeleton && (
                      <div className="space-y-3 py-4" aria-busy="true">
                        <Skeleton className="h-4 w-3/4 max-w-md" />
                        <Skeleton className="h-14 w-full rounded-lg" />
                        <Skeleton className="h-14 w-full rounded-lg" />
                        <Skeleton className="h-14 w-full rounded-lg" />
                      </div>
                    )}
                    {/* PILNE: Zadania koordynatora (coordinator_single) - na górze, wyróżnione żółtą ramką */}
                    {coordinatorTasks.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide font-semibold">
                          ⚠️ PILNE (Z Koordynatora)
                        </h4>
                        <div className="space-y-2">
                          {coordinatorTasks.map(task => (
                            <div
                              key={task.id}
                              className="rounded-lg border-2 border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-950/20 p-1"
                            >
                              <ChecklistItem
                                task={task}
                                disabled={!isWorkStarted}
                                isSaving={isSaving}
                                onToggle={handleToggleTask}
                                onNoteChange={handleNoteChange}
                                onPhotoTaken={handlePhotoTaken}
                                onPhotoUpload={handlePhotoUpload}
                                onPhotoDelete={handlePhotoDelete}
                                onDelete={handleDeleteExtraTask}
                                onRescheduleTask={undefined} // Koordynator nie może przenosić
                                onUndoMove={undefined} // Koordynator nie może cofać przeniesienia
                                onUndoComplete={task.completed ? handleUndoComplete : undefined}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* STANDARD: Zadania SOP (sop_standard) - środek listy */}
                    {standardTasks.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 uppercase tracking-wide">
                          {t('sections.recurring')} - Standardowe
                        </h4>
                        <div className="space-y-2">
                          {standardTasks.map(task => (
                            <ChecklistItem
                              key={task.id}
                              task={task}
                              disabled={!isWorkStarted || task.status === 'moved'}
                              isSaving={isSaving}
                              onToggle={handleToggleTask}
                              onNoteChange={handleNoteChange}
                              onPhotoTaken={handlePhotoTaken}
                              onPhotoUpload={handlePhotoUpload}
                              onPhotoDelete={handlePhotoDelete}
                              onDelete={undefined} // Standardowe zadania nie można usuwać
                              onRescheduleTask={task.status === 'moved' ? undefined : handleRescheduleTask}
                              onUndoMove={task.status === 'moved' && task.task_type === 'sop_standard' ? handleUndoMove : undefined}
                              onUndoComplete={task.completed ? handleUndoComplete : undefined}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* DODATKOWE: Zadania dodatkowe (is_extra_task) - pod standardowymi */}
                    {extraTasks.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 uppercase tracking-wide">
                          Zadania Dodatkowe
                        </h4>
                        <div className="space-y-2">
                          {extraTasks.map(task => (
                            <ChecklistItem
                              key={task.id}
                              task={task}
                              disabled={!isWorkStarted}
                              isSaving={isSaving}
                              onToggle={handleToggleTask}
                              onNoteChange={handleNoteChange}
                              onPhotoTaken={handlePhotoTaken}
                              onPhotoUpload={handlePhotoUpload}
                              onPhotoDelete={handlePhotoDelete}
                              onDelete={handleDeleteExtraTask}
                              onRescheduleTask={undefined} // Zadania dodatkowe nie można przenosić
                              onUndoMove={undefined} // Zadania dodatkowe nie można cofać przeniesienia
                              onUndoComplete={task.completed ? handleUndoComplete : undefined}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Separator przed long_term */}
                    {longTermTasks.length > 0 && (coordinatorTasks.length > 0 || standardTasks.length > 0 || extraTasks.length > 0) && (
                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-zinc-300 dark:border-zinc-600" />
                        </div>
                      </div>
                    )}

                    {/* OKRESOWE: Zadania długoterminowe (long_term) - na dole */}
                    {longTermTasks.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wide">
                          Okresowe (Długoterminowe)
                        </h4>
                        <div className="space-y-2">
                          {longTermTasks.map(task => (
                            <ChecklistItem
                              key={task.id}
                              task={task}
                              disabled={!isWorkStarted}
                              isSaving={isSaving}
                              onToggle={handleToggleTask}
                              onNoteChange={handleNoteChange}
                              onPhotoTaken={handlePhotoTaken}
                              onPhotoUpload={handlePhotoUpload}
                              onPhotoDelete={handlePhotoDelete}
                              onDelete={undefined} // Zadania długoterminowe nie można usuwać
                              onRescheduleTask={undefined} // Zadania długoterminowe nie można przenosić
                              onUndoMove={undefined} // Zadania długoterminowe nie można cofać przeniesienia
                              onUndoComplete={task.completed ? handleUndoComplete : undefined}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Formularz do dodawania zadań dodatkowych */}
                    {canAddAdhocTask && (
                      <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700 space-y-2">
                        <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 uppercase tracking-wide">
                          Dodaj zadanie dodatkowe
                        </h4>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input
                              value={newTaskName}
                              onChange={(e) => setNewTaskName(e.target.value)}
                              placeholder="Wpisz zadanie dodatkowe..."
                              className="flex-1 pr-10"
                              disabled={isSaving || isReadOnly}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleAddTask();
                                }
                              }}
                            />
                            {/* Przycisk mikrofonu dla głosowego wpisywania */}
                            {typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={isSaving || isReadOnly}
                                className={cn(
                                  "absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7",
                                  isListeningExtraTask && "bg-red-100 text-red-600 dark:bg-red-950 dark:text-red-400 animate-pulse"
                                )}
                                onClick={handleVoiceInputForExtraTask}
                                title={isListeningExtraTask ? 'Zatrzymaj dyktowanie' : 'Dyktuj nazwę zadania'}
                              >
                                <Mic className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          <Button
                            onClick={handleAddTask}
                            disabled={isSaving || isReadOnly || !newTaskName.trim()}
                            size="icon"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="issues"
              forceMount
              className="mt-4 outline-none data-[state=inactive]:hidden"
            >
              <div
                className={cn(
                  "relative rounded-xl overflow-hidden transition-all border-2 bg-white shadow-md",
                  "dark:bg-zinc-900",
                  "border-amber-400 dark:border-amber-700"
                )}
              >
                <div className="px-4 py-3 border-b-2 border-amber-200 dark:border-amber-800 flex items-center gap-3 bg-amber-100 dark:bg-amber-950/60">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-200 dark:bg-amber-900/60">
                    <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-bold text-amber-950 dark:text-amber-100">{t('sections.issues')}</h3>
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                      {t('section.reportIssueInSection')}
                    </p>
                  </div>
                </div>
                <div className="px-4 pb-4 pt-4">
                  <IssueReportSection
                    disabled={false}
                    locationId={locationId}
                    sectionId={section?.id}
                    sectionName={section?.name}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <AlertDialog
        open={Boolean(extraTaskToDelete)}
        onOpenChange={(open) => {
          if (!open) setExtraTaskToDelete(null);
        }}
      >
        <AlertDialogContent className="z-[90]">
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć zadanie dodatkowe?</AlertDialogTitle>
            <AlertDialogDescription>
              {extraTaskToDelete?.name
                ? `Zadanie „${extraTaskToDelete.name}” zniknie z listy tej sekcji.`
                : 'Zadanie zniknie z listy tej sekcji.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isSaving}
              onClick={(event) => {
                event.preventDefault();
                void confirmDeleteExtraTask();
              }}
            >
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
